import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Bell, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import PaginationBar from "../components/PaginationBar";
import { toast } from "sonner";
import { useAuth } from "../auth";
import { notificationTarget } from "../lib/notificationTarget";

export default function NotificationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0); // 0-indexed
  const nav = useNavigate();

  const reload = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 500 } });
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const click = async (n) => {
    try { if (!n.read) await api.post(`/notifications/${n.notification_id}/read`); }
    catch (e) { console.warn("Notif: échec mark-read", n.notification_id, e?.response?.status); }
    nav(notificationTarget(n, user?.role));
  };

  const markAllRead = async () => {
    try {
      const r = await api.post("/notifications/mark-all-read");
      toast.success(`${r.data?.updated ?? ""} notification(s) marquée(s) comme lues`);
    } catch (e) {
      console.warn("mark-all-read failed", e?.response?.status);
      toast.error("Échec du marquage — réessayez");
    }
    await reload();
  };

  const unreadCount = items.filter((n) => !n.read).length;
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const pageItems = items.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Bell className="w-6 h-6 text-sky-700" />
          Notifications
          {unreadCount > 0 && <span className="text-sm bg-red-500 text-white rounded-full px-2 py-0.5">{unreadCount}</span>}
        </h1>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" className="rounded-full" onClick={markAllRead} data-testid="notifs-mark-all">
            <Check className="w-3 h-3 mr-1" />Tout marquer lu
          </Button>
        )}
      </div>

      {loading ? (
        <div className="card-soft p-8 text-center text-slate-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="card-soft p-8 text-center text-slate-500" data-testid="notifs-empty">
          Aucune notification pour le moment.
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {pageItems.map((n) => (
              <button
                key={n.notification_id}
                onClick={() => click(n)}
                className={`card-soft w-full text-left p-4 transition hover:border-sky-300 ${
                  !n.read ? "bg-sky-50/60 border-l-4 border-l-sky-600" : ""
                }`}
                data-testid={`notif-page-item-${n.notification_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{n.title}</div>
                    <div className="text-sm text-slate-600 mt-1">{n.body}</div>
                    <div className="text-xs text-slate-400 mt-2">{new Date(n.created_at).toLocaleString("fr-FR")}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-sky-600 mt-2 shrink-0" />}
                </div>
              </button>
            ))}
          </div>
          <PaginationBar
            page={page} setPage={setPage} totalPages={totalPages}
            pageSize={pageSize} setPageSize={(v) => { setPageSize(v); setPage(0); }}
            total={items.length} testId="notifs"
          />
        </>
      )}
    </div>
  );
}
